import { is } from '@common/is/is'

import type { TLanguages, TTranslateEventName, TLanguageChangeCallback, ITranslatorEventCallbacks } from '../definitions'

import { TRANSLATE_CHANGED_EVENT } from '../definitions'
import { LANG_TITLES }             from '../consts'

import isValidRegionCode from '../v1/isValidRegionCode'
import createNamespace   from '../v1/createNamespace'
import languageHandler   from '../v1/languageHandler'
import localeHandler     from '../v1/localeHandler'
import getTransRaw       from '../v1/getTransRaw'
import getTrans          from '../v1/getTrans'
import trans             from '../v1/trans'

let availableLanguages: TLanguages | undefined

// eslint-disable-next-line @typescript-eslint/no-use-before-define
let translator: Translator | undefined

export default class Translator<T extends TLanguages = TLanguages> {
  public isValidRegionCode = isValidRegionCode
  public createNamespace   = createNamespace
  public getTransByLang    = getTrans
  public addLocale         = localeHandler.addLocale
  public trans             = trans

  private readonly eventCallbacks: ITranslatorEventCallbacks = {
    change: []
  }

  public get actLang (): T[number] {
    return languageHandler.getLang()
  }

  public set actLang (lang: string | null | undefined) {
    const html = document.querySelector('html')

    if (is.instanceOf(html, HTMLElement) && is.string(lang)) {
      html.setAttribute('lang', lang)
    }

    languageHandler.setLang(lang)
  }

  public get hasMultipleLangs (): boolean {
    return (availableLanguages ?? []).length > 1
  }

  protected constructor (languages?: T) {
    availableLanguages = languages

    if (languages) {
      languageHandler.init(languages)
    }

    document.addEventListener(TRANSLATE_CHANGED_EVENT, this.onChange)
  }

  /**
   * Singleton példányosítás.
   * @param languages - Az opcionális, lehetséges nyelvek.
   */
  public static instance <T extends TLanguages> (languages?: T): Translator<T> {
    if (!translator) {
      translator = new Translator<T>(languages)
    }

    return translator as Translator<T>
  }

  /**
   * Egy adott nyelv alapján visszaadja a hozzátartozó nevet.
   * @param lang - A nyelv.
   * @example
   * ```js
   * hu → magyar
   * en → english
   * ```
   */
  public getLangTitle (this: void, lang: T[number]): string {
    return LANG_TITLES[lang]
  }

  /**
   * Van nyelvesítés az adott id-hoz?
   * @param id - Az azonosító.
   */
  public hasTrans (this: void, id: string): boolean {
    const value = getTransRaw(id)

    return is.string(value) && !is.empty(value)
  }

  /**
   * Visszaadja a sorban a következő nyelvet az engedélyezett nyelvek közül.
   * Ha nincs további nyelv, akkor az elsőt adja.
   */
  public getNextLang (): T[number] {
    const allLangs = availableLanguages ?? []

    const actLangIndex  = allLangs.indexOf(this.actLang)         // Az aktuális nyelv indexe az összes választható nyelv közül.
    const nextLangIndex = actLangIndex + 1                       // A következő nyelv indexe.
    const nextLang      = allLangs[nextLangIndex] || allLangs[0] // Ha nincs következő nyelv, akkor elölről kezdjük.

    return nextLang
  }

  /**
   * Eseményre feliratkozás.
   * @param eventName - Az esemény neve.
   * @param callback  - A callback, ami majd meg lesz hívva az esemény bekövetkeztekor.
   */
  public on (eventName: TTranslateEventName, callback: TLanguageChangeCallback): void {
    this.eventCallbacks[eventName].push(callback)
  }

  /**
   * Eseményről leiratkozás.
   * @param eventName         - Az esemény neve.
   * @param removableCallback - A callback, amit kiveszünk a meghívandók közül.
   */
  public off (eventName: TTranslateEventName, removableCallback: TLanguageChangeCallback): void {
    const remainingCallbacks = this.eventCallbacks[eventName].filter((callback) => {
      return callback !== removableCallback
    })

    this.eventCallbacks[eventName] = remainingCallbacks
  }

  /**
   * Ha megváltozik az aktuális nyelv, akkor ez a callback hívódik meg,
   * ami aztán az összes feliratkozót meghívja.
   */
  private readonly onChange = (): void => {
    this.eventCallbacks.change.forEach((callback) => {
      callback(this.actLang)
    })
  }
}
